import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Select,
  Button,
  Form,
  DatePicker,
  Typography,
  Row,
  Col,
} from "antd";
import { QrcodeOutlined } from "@ant-design/icons";
import { useAuth } from "../../../services/auth";
import { updateWarehouseDevice } from "../../../services/warehouse-devices";
import { QrReader } from "react-qr-reader";
import dayjs from "dayjs";

const { Option } = Select;

const EditDeviceForm = ({ open, deviceData, onClose, onEditComplete }) => {
  const [user] = useAuth();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (deviceData) {
      // Danh sách các hệ số cần có
      const defaultCoeffs = {
        a_ec: 0,
        b_ec: 1,
        c_ec: 0,
        a_ph: 0,
        b_ph: 1,
        c_ph: 0,
        a_sm: 0,
        b_sm: 1,
        c_sm: 0,
      };
      // Merge hệ số từ backend với mặc định
      const loadedCoeffs = { ...defaultCoeffs, ...(deviceData.coefficients || {}) };
      // Ép kiểu về float cho chắc chắn
      Object.keys(loadedCoeffs).forEach((key) => {
        loadedCoeffs[key] = isNaN(parseFloat(loadedCoeffs[key]))
          ? defaultCoeffs[key]
          : parseFloat(loadedCoeffs[key]);
      });

      form.setFieldsValue({
        ...deviceData,
        imported_at: deviceData.imported_at ? dayjs(deviceData.imported_at) : null,
        tested_at: deviceData.tested_at ? dayjs(deviceData.tested_at) : null,
        coefficients: loadedCoeffs,
      });
    }
  }, [deviceData, form]);

  const handleQrScan = (result, error) => {
    if (result) {
      form.setFieldValue("qr_string", result.text);
      setScannerOpen(false);
    }
    if (error) console.error(error);
  };

  const handleSubmit = async (values) => {
    try {
      // Xử lý coefficients: ép kiểu về float, nếu không hợp lệ thì trả về null
      const coeffs = { ...values.coefficients };
      Object.keys(coeffs).forEach((key) => {
        const val = coeffs[key];
        coeffs[key] =
          val === "" || isNaN(parseFloat(val)) ? null : parseFloat(val);
      });

      const payload = {
        ...values,
        id: deviceData.id,
        imported_at: values.imported_at?.format("YYYY-MM-DD") || null,
        tested_at: values.tested_at?.format("YYYY-MM-DD") || null,
        user: user?.email || "",
        coefficients: coeffs,
      };

      // Nếu có hệ số null thì cảnh báo và không gửi
      if (Object.values(coeffs).some((v) => v === null)) {
        Modal.error({
          title: "Lỗi nhập hệ số",
          content: "Tất cả các hệ số phải là số thực hợp lệ!",
        });
        return;
      }

      const res = await updateWarehouseDevice(payload);
      if (!!res) {
        onEditComplete();
        onClose();
      } else {
        console.error("Failed to edit device");
      }
    } catch (err) {
      console.error("Error editing device: ", err);
    }
  };

  return (
    <>
      <Modal
        title={
          <Typography.Title level={4}>Chỉnh sửa thiết bị</Typography.Title>
        }
        open={open}
        onCancel={onClose}
        footer={null}
        width="100%"
        style={{ maxWidth: "85vw", margin: "0 auto" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            qr_string: "",
            device_type: "",
            status: 2,
            lot: "3",
            imported_at: null,
            tested_at: null,
            group: "3",
            note: "",
            coefficients: {
              a_ec: 0,
              b_ec: 1,
              c_ec: 0,
              a_ph: 0,
              b_ph: 1,
              c_ph: 0,
              a_sm: 0,
              b_sm: 1,
              c_sm: 0,
            },
          }}
        >
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={24} lg={8}>
              <Form.Item
                name="qr_string"
                label="Mã QR"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="QR Code"
                  size="large"
                  suffix={
                    <Button
                      size="small"
                      type="link"
                      icon={<QrcodeOutlined />}
                      onClick={() => setScannerOpen(true)}
                    />
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item
                name="device_type"
                label="Loại thiết bị"
                rules={[{ required: true }]}
              >
                <Select placeholder="Chọn loại thiết bị" size="large">
                  {["S3", "S7", "S4", "LN", "BLE", "GW"].map((type, idx) => (
                    <Option key={idx} value={idx}>
                      {type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item
                name="status"
                label="Trạng thái"
                rules={[{ required: true }]}
              >
                <Select placeholder="Chọn trạng thái" size="large">
                  {[
                    { label: "Khởi tạo", value: 0 },
                    { label: "Kiểm tra", value: 1 },
                    { label: "Tồn kho", value: 2 },
                    { label: "Kích hoạt", value: 3 },
                    { label: "Hoàn trả", value: 4 },
                  ].map((status) => (
                    <Option
                      key={status.value}
                      value={status.value}
                      disabled={status.value === 3}
                    >
                      {status.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item name="lot" label="Lô nhập">
                <Input placeholder="Lot" size="large" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item name="group" label="Nhóm">
                <Input placeholder="Group" size="large" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item name="imported_at" label="Ngày nhập">
                <DatePicker
                  placeholder="Chọn ngày nhập"
                  size="large"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={8}>
              <Form.Item name="tested_at" label="Ngày kiểm tra">
                <DatePicker
                  placeholder="Chọn ngày kiểm tra"
                  size="large"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} lg={16}>
              <Form.Item name="note" label="Ghi chú">
                <Input.TextArea placeholder="Note" size="large" rows={1} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "a_ec"]}
                label="a_ec"
                rules={[
                  { required: true, message: "Nhập a_ec!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="a_ec" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "b_ec"]}
                label="b_ec"
                rules={[
                  { required: true, message: "Nhập b_ec!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={1}
              >
                <Input placeholder="b_ec" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "c_ec"]}
                label="c_ec"
                rules={[
                  { required: true, message: "Nhập c_ec!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="c_ec" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "a_ph"]}
                label="a_ph"
                rules={[
                  { required: true, message: "Nhập a_ph!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="a_ph" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "b_ph"]}
                label="b_ph"
                rules={[
                  { required: true, message: "Nhập b_ph!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={1}
              >
                <Input placeholder="b_ph" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "c_ph"]}
                label="c_ph"
                rules={[
                  { required: true, message: "Nhập c_ph!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="c_ph" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "a_sm"]}
                label="a_sm"
                rules={[
                  { required: true, message: "Nhập a_sm!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="a_sm" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "b_sm"]}
                label="b_sm"
                rules={[
                  { required: true, message: "Nhập b_sm!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={1}
              >
                <Input placeholder="b_sm" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <Form.Item
                name={["coefficients", "c_sm"]}
                label="c_sm"
                rules={[
                  { required: true, message: "Nhập c_sm!" },
                  {
                    validator: (_, value) =>
                      isNaN(parseFloat(value))
                        ? Promise.reject("Phải là số thực!")
                        : Promise.resolve(),
                  },
                ]}
                initialValue={0}
              >
                <Input placeholder="c_sm" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Lưu thay đổi
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* QR Scanner Modal */}
      <Modal
        title={<Typography.Title level={4}>Quét mã QR</Typography.Title>}
        open={scannerOpen}
        onCancel={() => setScannerOpen(false)}
        footer={null}
        width="100%"
        style={{ maxWidth: "400px", margin: "0 auto" }}
      >
        <QrReader
          delay={300}
          onResult={handleQrScan}
          constraints={{ facingMode: "environment" }}
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
};

export default EditDeviceForm;
